import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Circle,
  Loader2,
  RefreshCw,
  TriangleAlert,
} from "lucide-react";

const SEV = {
  high: {
    label: "Act today",
    chip: "bg-rose-400/10 border-rose-400/40 text-rose-300",
    bar: "bg-rose-400",
    Icon: TriangleAlert,
    iconClass: "text-rose-400",
  },
  medium: {
    label: "This week",
    chip: "bg-amber-400/10 border-amber-400/40 text-amber-300",
    bar: "bg-amber-400",
    Icon: AlertTriangle,
    iconClass: "text-amber-400",
  },
  low: {
    label: "Housekeeping",
    chip: "bg-brand/10 border-brand/40 text-brand",
    bar: "bg-brand",
    Icon: Circle,
    iconClass: "text-brand",
  },
};

export default function ExceptionInbox() {
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/exceptions?currency=${currency}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the exception inbox");
    } finally {
      setLoading(false);
    }
  }, [currency]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5" data-testid="inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Exception Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Everything the books are waiting on, in one place — so nothing sits unactioned.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2 h-10" data-testid="inbox-refresh">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {loading || !data ? (
        <div className="py-24 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : data.all_clear ? (
        <Card className="p-12 text-center space-y-3" data-testid="inbox-all-clear">
          <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
          <p className="font-heading text-xl">Books are clean</p>
          <p className="text-sm text-muted-foreground">
            No unmatched bank lines, no overdue invoices, every day signed off.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Act today", data.counts.high, "inbox-count-high"],
              ["This week", data.counts.medium, "inbox-count-medium"],
              ["Housekeeping", data.counts.low, "inbox-count-low"],
              ["Open items", data.total_open, "inbox-count-total"],
            ].map(([label, value, tid]) => (
              <Card key={tid} className="p-4" data-testid={tid}>
                <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
                <p className="font-heading text-2xl mt-1.5">{value}</p>
              </Card>
            ))}
          </div>

          <div className="space-y-3">
            {data.items.map((item) => {
              const sev = SEV[item.severity];
              return (
                <Card
                  key={item.id}
                  className="relative overflow-hidden p-5 pl-6"
                  data-testid={`inbox-item-${item.id}`}
                >
                  <span className={`absolute left-0 top-0 h-full w-[3px] ${sev.bar}`} />
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <sev.Icon className={`h-5 w-5 mt-0.5 shrink-0 ${sev.iconClass}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium">{item.title}</p>
                          <span
                            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${sev.chip}`}
                          >
                            {sev.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{item.detail}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-heading text-xl" data-testid={`inbox-item-count-${item.id}`}>
                          {item.count}
                        </p>
                        {item.value != null && (
                          <p className="text-[11px] font-mono text-muted-foreground">
                            {formatMoney(item.value, currency, { compact: true })}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate(item.route)}
                        className="gap-1.5"
                        data-testid={`inbox-action-${item.id}`}
                      >
                        {item.action}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {item.samples?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                      {item.samples.map((s, i) => (
                        <div
                          key={`${item.id}-${i}`}
                          className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-2 min-w-0"
                        >
                          <p className="text-xs truncate">{s.label}</p>
                          <p className="text-[11px] font-mono text-muted-foreground truncate">{s.sub}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground font-mono" data-testid="inbox-generated">
            Generated {new Date(data.generated_at).toLocaleString()}
          </p>
        </>
      )}
    </div>
  );
}
